import { useEffect, useState } from 'react';
import {
  LOBBY_HISTORY,
  type CrashPhase,
  type CrashRoundState,
  type CrashSnapshot,
  type LiveWin,
  type LobbyCrash,
  type LobbyLiveResponse,
} from '@tg/shared';
import { endpoints, type TickerItem } from '@/api/endpoints';
import { useSession, selectReducedMotion } from '@/stores/session';
import { emitAck, getSocket } from '@/socket/socket';

/**
 * Everything the lobby shows as "live". `data === null` — nothing from the server (offline, first request pending):
 * the lobby shows a neutral state ("—", empty strip / donut), never made-up activity (owner decision №12).
 */
export interface LobbyLiveState {
  data: LobbyLiveResponse | null;
  /** estimated server clock − Date.now() */
  offset: number;
  /** Liftoff counters (players / stakes / cashed) belong to another round than the one shown → "—" until the next poll */
  crashCountersStale: boolean;
}

export const POLL_MS = 3_000;
export const REQUEST_TIMEOUT_MS = 3_000;

/** Server live wins → ticker items (roulette ×14 is green; ×2 colour is unknown from the bet row). */
export function winsToTicker(wins: LiveWin[]): TickerItem[] {
  return wins.map((w) => ({
    id: w.betId,
    nickname: w.nickname,
    amount: w.payout,
    game: w.game,
    multiplier: w.game === 'crash' ? w.multiplier : null,
    color: w.game === 'roulette' && w.multiplier !== null && w.multiplier >= 14 ? 'green' : null,
  }));
}

const PHASE_ORDER: Record<CrashPhase, number> = { betting: 0, flying: 1, crashed: 2 };

/**
 * Liftoff phase changes arrive over the socket (`crash:round`) the moment they happen — the poll alone would let the
 * lobby curve keep growing up to POLL_MS past the real crash. Counters stay from the poll (same round only).
 */
export function mergeCrash(
  polled: LobbyCrash | null,
  pushed: CrashRoundState | null,
): { crash: LobbyCrash | null; stale: boolean } {
  if (!polled || !pushed) return { crash: polled, stale: false };
  const newer =
    pushed.roundNo > polled.roundNo ||
    (pushed.roundNo === polled.roundNo && PHASE_ORDER[pushed.phase] > PHASE_ORDER[polled.phase]);
  if (!newer) return { crash: polled, stale: false };
  const history =
    pushed.phase === 'crashed' &&
    pushed.crashPoint !== null &&
    !polled.history.some((h) => h.roundNo === pushed.roundNo)
      ? [{ roundNo: pushed.roundNo, crashPoint: pushed.crashPoint }, ...polled.history].slice(
          0,
          LOBBY_HISTORY,
        )
      : polled.history;
  return {
    crash: {
      ...polled,
      roundNo: pushed.roundNo,
      phase: pushed.phase,
      bettingEndsAt: pushed.bettingEndsAt,
      startedAt: pushed.startedAt,
      crashPoint: pushed.crashPoint,
      history,
    },
    stale: pushed.roundNo !== polled.roundNo,
  };
}

/** Polls GET /api/lobby/live (the server shares one cached response between all lobbies) + Liftoff phase pushes. */
export function useLobbyLive(): LobbyLiveState {
  const apiAvailable = useSession((s) => s.apiAvailable);
  const [polled, setPolled] = useState<{ data: LobbyLiveResponse; offset: number } | null>(null);
  const [pushed, setPushed] = useState<{ round: CrashRoundState; offset: number } | null>(null);

  useEffect(() => {
    if (!apiAvailable) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let active: AbortController | undefined;
    const cancel = () => {
      clearTimeout(timer);
      clearTimeout(timeout);
      active?.abort();
      active = undefined;
    };
    const tick = async () => {
      cancel();
      if (stop || document.visibilityState === 'hidden') return;
      const ctrl = new AbortController();
      active = ctrl;
      // A stalled fetch must not leave an old flying round growing indefinitely.
      timeout = setTimeout(() => {
        if (active !== ctrl) return;
        active = undefined;
        ctrl.abort();
        setPolled(null);
        timer = setTimeout(tick, POLL_MS);
      }, REQUEST_TIMEOUT_MS);
      try {
        const data = await endpoints.lobbyLive({ signal: ctrl.signal });
        if (active !== ctrl || stop) return;
        setPolled({ data, offset: data.serverNow - Date.now() });
      } catch {
        if (active !== ctrl || stop) return;
        setPolled(null);
      }
      clearTimeout(timeout);
      active = undefined;
      if (!stop) timer = setTimeout(tick, POLL_MS);
    };
    const refresh = () => {
      cancel();
      setPolled(null);
      setPushed(null);
      void tick();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('online', refresh);
    void tick();
    return () => {
      stop = true;
      cancel();
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [apiAvailable]);

  useEffect(() => {
    if (!apiAvailable) return;
    const s = getSocket() as unknown as {
      connected: boolean;
      on(ev: string, fn: (...a: never[]) => void): unknown;
      off(ev: string, fn: (...a: never[]) => void): unknown;
      emit(ev: string, ...a: unknown[]): unknown;
    };
    let live = true;
    const onRound = (e: { round: CrashRoundState; serverNow: number }) => {
      if (live) setPushed({ round: e.round, offset: e.serverNow - Date.now() });
    };
    const join = () => {
      emitAck<CrashSnapshot>('crash:join')
        .then((snap) => {
          if (live && snap.round) setPushed({ round: snap.round, offset: snap.serverNow - Date.now() });
        })
        .catch(() => undefined);
    };
    s.on('crash:round', onRound);
    s.on('connect', join);
    if (s.connected) join();
    return () => {
      live = false;
      s.off('crash:round', onRound);
      s.off('connect', join);
      if (s.connected) s.emit('room:leave', 'crash');
    };
  }, [apiAvailable]);

  if (!apiAvailable || !polled) return { data: null, offset: 0, crashCountersStale: false };
  const { crash, stale } = mergeCrash(polled.data.crash, pushed?.round ?? null);
  // Use the clock of the source that actually supplied the round, not an older same-round push.
  const offset = pushed && crash !== polled.data.crash ? pushed.offset : polled.offset;
  return { data: { ...polled.data, crash }, offset, crashCountersStale: stale };
}

/** Countdown clock; the isolated flight chart uses at most 30 fps (4 fps with reduced motion). */
export function useNow(ms = 1000, fast = false): number {
  const reduced = useSession(selectReducedMotion);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const restart = () => {
      clearInterval(timer);
      if (document.visibilityState === 'hidden') return;
      setNow(Date.now());
      timer = setInterval(() => setNow(Date.now()), fast ? (reduced ? 250 : 34) : ms);
    };
    restart();
    document.addEventListener('visibilitychange', restart);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', restart);
    };
  }, [ms, fast, reduced]);
  // A fresh snapshot can arrive between clock ticks.
  return Math.max(now, Date.now());
}
